import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Save, Loader2 } from "lucide-react";

export default function CompanyProfileSetup() {
  return (
    <DashboardLayout>
      <CompanyProfileSetupContent />
    </DashboardLayout>
  );
}

function CompanyProfileSetupContent() {
  const { data: companies } = trpc.company.list.useQuery();
  const companyId = companies?.[0]?.id || 1;

  // Get existing profile
  const {
    data: profile,
    isLoading,
    refetch,
  } = trpc.deductionOptimizer.getCompanyProfile.useQuery({ companyId });

  // Form state - matches companyProfiles schema
  const [formData, setFormData] = useState({
    industrySector: profile?.industrySector || "",
    numberOfEmployees: profile?.numberOfEmployees || 0,
    annualRevenue: profile?.annualRevenue || 0,
    paidInCapital: profile?.paidInCapital || 0,
    legalReserves: profile?.legalReserves || 0,
    voluntaryReserves: profile?.voluntaryReserves || 0,
  });

  // Update mutation
  const updateMutation =
    trpc.deductionOptimizer.updateCompanyProfile.useMutation({
      onSuccess: () => {
        toast.success("Company profile updated successfully");
        refetch();
      },
      onError: error => {
        toast.error("Failed to update profile", {
          description: error.message,
        });
      },
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      companyId,
      ...formData,
    });
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container py-8 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <Building2 className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">Company Profile Setup</h1>
          <p className="text-muted-foreground">
            Configure extended company data for accurate tax optimization
            suggestions
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>
                Essential company details for tax filing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="industrySector">Industry Sector</Label>
                <Input
                  id="industrySector"
                  value={formData.industrySector}
                  onChange={e => handleChange("industrySector", e.target.value)}
                  placeholder="e.g., Technology, Manufacturing, Services"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="numberOfEmployees">Number of Employees</Label>
                  <Input
                    id="numberOfEmployees"
                    type="number"
                    value={formData.numberOfEmployees}
                    onChange={e =>
                      handleChange(
                        "numberOfEmployees",
                        parseInt(e.target.value) || 0
                      )
                    }
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="annualRevenue">
                    Annual Revenue (CHF, in cents)
                  </Label>
                  <Input
                    id="annualRevenue"
                    type="number"
                    value={formData.annualRevenue}
                    onChange={e =>
                      handleChange(
                        "annualRevenue",
                        parseInt(e.target.value) || 0
                      )
                    }
                    placeholder="0"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Financial Structure */}
          <Card>
            <CardHeader>
              <CardTitle>Financial Structure</CardTitle>
              <CardDescription>
                Capital and reserves (all amounts in cents/rappen)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="paidInCapital">
                  Paid-in Capital (CHF cents)
                </Label>
                <Input
                  id="paidInCapital"
                  type="number"
                  value={formData.paidInCapital}
                  onChange={e =>
                    handleChange("paidInCapital", parseInt(e.target.value) || 0)
                  }
                  placeholder="0"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="legalReserves">
                    Legal Reserves (CHF cents)
                  </Label>
                  <Input
                    id="legalReserves"
                    type="number"
                    value={formData.legalReserves}
                    onChange={e =>
                      handleChange(
                        "legalReserves",
                        parseInt(e.target.value) || 0
                      )
                    }
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="voluntaryReserves">
                    Voluntary Reserves (CHF cents)
                  </Label>
                  <Input
                    id="voluntaryReserves"
                    type="number"
                    value={formData.voluntaryReserves}
                    onChange={e =>
                      handleChange(
                        "voluntaryReserves",
                        parseInt(e.target.value) || 0
                      )
                    }
                    placeholder="0"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button type="submit" size="lg" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
